import Api from "../../services/Api.ts";
import {useEffect, useState} from "react";
import {useAtom, useSetAtom} from 'jotai';
import { categoriesAtom, selectedDepartmentsAtom, matchingClientsAtom } from '../../jotai_state';
import TooFewClients from "../../components/TooFewClients/TooFewClients.tsx";
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import PercentBar from "../../components/Bars/PercentBar.tsx";
import ValueBar from "../../components/Bars/ValueBar.tsx";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);

interface StressInterface {
  title: string
  nr: number
  percent: number
}

interface PersonalityInterface {
  [index: string]: number;
}

interface AverageInterface {
  [trait: string]: number;
}

export default function SprintProfile() {
  const api = Api.getInstance();
  const [tooFewClients, setTooFewClients] = useState<number|undefined>(undefined);
  const [stress, setStress] = useState<Array<StressInterface>>([]);
  const [personalities, setPersonalities] = useState<Array<PersonalityInterface>>([]);
  const [groupAverage, setGroupAverage] = useState<AverageInterface>({});
  const [totalAverage, setTotalAverage] = useState<AverageInterface>({});
  const [categories] = useAtom(categoriesAtom);
  const [selectedDepartments] = useAtom(selectedDepartmentsAtom);
  const setMatchingClients = useSetAtom(matchingClientsAtom);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getData()
  }, [categories, selectedDepartments]);

  const getData = () => {
    const data = {
      categories: categories,
      departments: selectedDepartments,
    }

    api.post('customer/sprint_profile', data).then((response) => {

      if (response.json.data.tooFewClients !== undefined) {
        setTooFewClients(response.json.data.tooFewClients)
        setMatchingClients(response.json.data.tooFewClients);
      } else {
        setStress(response.json.data.stress);
        setPersonalities(response.json.data.personalities);
        setGroupAverage(response.json.data.groupAverage);
        setTotalAverage(response.json.data.totalAverage);
        setMatchingClients(response.json.data.matchingClients);
        setTooFewClients(undefined);
      }
      setLoaded(true);
    }).catch(error => {
      setError('Unknown error (http status ' + error.response.status + ')')
      setLoaded(true)
    })
  }

  const renderStress = () => {
    let respondents = 0;
    for (const s of stress) {
      respondents += s.nr;
    }

    return (
      <>
        <div className="z-h3 mt-10">Stress levels</div>
        <div className="mt-4 z-box large white">

          <div className="flex gap-x-4 mt-8">
            <div className="z-box w-1/4">
              <div className="z-h1">{respondents}</div>
              <div className="z-body1 text-gray-800!">respondents</div>
            </div>
            <div className="z-box w-1/4">
              <div className="z-h1">{stress[0].percent.toFixed(0)}%</div>
              <div className="z-body1 text-gray-800!">low stress</div>
            </div>
            <div className="z-box w-1/4">
              <div className="z-h1">{stress[1].percent.toFixed(0)}%</div>
              <div className="z-body1 text-gray-800!">medium stress</div>
            </div>
            <div className="z-box w-1/4">
              <div className="z-h1">{stress[2].percent.toFixed(0)}%</div>
              <div className="z-body1 text-gray-800!">high stress</div>
            </div>
          </div>

          <div className="flex items-center mt-6">
            <div className="z-title3 w-20">Low</div>
            <ValueBar value={stress[0].percent} max={100} color="aquamarine-600"/>
            <div className="ml-4 z-body2">100%</div>
          </div>

          <div className="flex items-center mt-6">
            <div className="z-title3 w-20">Medium</div>
            <ValueBar value={stress[1].percent} max={100} color="orange-500"/>
            <div className="ml-4 z-body2">100%</div>
          </div>

          <div className="flex items-center mt-6">
            <div className="z-title3 w-20">High</div>
            <ValueBar value={stress[2].percent} max={100} color="cherry-600"/>
            <div className="ml-4 z-body2">100%</div>
          </div>

        </div>
      </>
    )
  }

  const personalityNames: { [trait: string]: string } = {
    O: "Openeness",
    E: "Extraversion",
    N: "Neuroticism",
    A: "Agreeableness",
    C: "Conscientioness"
  }

  const scoreToPosition = (score: number) => {
    // Subtract 24 to mid of first column, then 4 to start of column
    // Divide by interval width (8)
    return (score - 28) / 8;
  }

  const renderTrait = (trait: string) => {
    const start = 24;
    const interval = 8;
    const labels: Array<string> = [];
    const data = [];
    const name = personalityNames[trait];
    let from = start;
    let to = start + interval;
    let min = 120;
    let max = 0;
    for (let i = start; i < 120; i += interval) {
      labels.push(from + "-" + to);
      from = to + 1;
      to = from + interval - 1;
      data.push(0);
    }

    for (const p of personalities) {
      const score = p[trait];
      const pos = score < 33 ? 0 : Math.floor((score - 25) / 8);
      // console.log(score + " pos " + pos);
      data[pos] += 100 / personalities.length;
      if (min > score) min = score;
      if (max < score) max = score;
    }

    const groupAvg = groupAverage[trait];
    const totalAvg = totalAverage[trait];

    return (
      <div key={"trait_" + trait} className="mt-4">
        <div className="z-h3">{name}</div>
        <div className="z-box large white">
          <div className="flex gap-x-4 my-8">
            <div className="z-box w-1/4">
              <div className="z-h1">{personalities.length}</div>
              <div className="z-body1 text-gray-800!">respondents</div>
            </div>
            <div className="z-box w-1/4">
              <div className="z-h1">{groupAvg.toFixed(0)}</div>
              <div className="z-body1 text-gray-800!">Average score</div>
            </div>
            <div className="z-box w-1/4">
              <div className="z-h1">{totalAvg.toFixed(0)}</div>
              <div className="z-body1 text-gray-800!">Benchmark</div>
            </div>
            <div className="z-box w-1/4">
              <div className="z-h1">{min}-{max}</div>
              <div className="z-body1 text-gray-800!">Observed rate</div>
            </div>
          </div>

          <Bar
            data={{
              labels: labels,
              datasets: [{
                label: name,
                data: data,
                backgroundColor: '#6E89F5',
              }],
            }}
            options={{
              responsive: true,
              scales: {y: {min: 0, ticks: {callback: (v) => v + '%'}}},
              plugins: {
                legend: {display: false},
                title: {display: false},
                annotation: {
                  annotations: {
                    groupMean: {
                      type: 'line',
                      xMin: scoreToPosition(groupAvg),
                      xMax: scoreToPosition(groupAvg),
                      borderColor: '#ff9800',
                      borderWidth: 2,
                      borderDash: [5, 5],
                      label: {display: true, content: "Group"}
                    },
                    totalMean: {
                      type: 'line',
                      xMin: scoreToPosition(totalAvg),
                      xMax: scoreToPosition(totalAvg),
                      borderColor: '#0098ff',
                      borderWidth: 2,
                      borderDash: [5, 5],
                      label: {display: false}
                    }
                  }
                }
              },
            }}
          />

        </div>
      </div>
    )
  }

  const renderPersonalities = () => {
    const traits = ["O", "E", "C", "A", "N"]
    return (
      <>
        <div className="mt-8 z-h3">Personality traits</div>
        {
          traits.map(trait => renderTrait(trait))
        }
      </>
    )
  }

  const renderMain = () => {

    if (!loaded) {
      return <div>Loading data...</div>
    }
    if (error.length > 0) {
      return <div>{error}</div>
    }
    if (tooFewClients !== undefined) {
      return <TooFewClients tooFewClients={tooFewClients}/>
    }

    return (
      <>
        <div className="z-h2">Profile data</div>
        <div className="z-body1 mt-4 text-gray-1000!">
          How diverse the personality traits of the group affect their ability to work in and drive the initiative.
        </div>
        {renderStress()}
        {renderPersonalities()}
      </>
    )
  }

  return (
    <>
      {renderMain()}
    </>
  );
}